/**
 * signing domain — /signing/* (15 routes) + /v3/signing/correct-signer (PLANNED).
 *
 * Gotcha: /signing/wet-complete returns a DIFFERENT body shape on 422
 * (`{success:false, reason, error}`) than on success
 * (`{Success, CartId, MarkedAt, ...}`). Our http.ts treats non-2xx as ApiError,
 * so the 422 body arrives as `ApiError.details` with `code` set to the reason.
 */

import type {
  RequestOptions,
  PreparePackageRequest,
  PreparePackageResult,
  PrepareStatusResult,
  CachePdfsRequest,
  CachePdfsResult,
  CreateSigningSessionRequest,
  CreateSigningSessionResponse,
  SendInviteRequest,
  InviteResult,
  StartSigningRequest,
  StartSigningResponse,
  SigningSessionStatusResponse,
  DownloadResult,
  GenerateQrRequest,
  SigningLinkResult,
  CreateEmbeddedInvitesRequest,
  EmbeddedInvitesResponse,
  EmbeddedInviteLinkRequest,
  EmbeddedInviteLinkResponse,
  WetCompleteRequest,
  WetCompleteSuccessResponse,
  CorrectionRequestInput,
  CorrectionRequestResult,
  CorrectionStatusResult,
  CorrectionHistoryResult,
  CorrectSignerInput,
  CorrectSignerResult,
  EsignConsentStatus,
  EsignConsentRecordInput,
  EsignConsentRecordResponse,
  EsignConsentTextResponse,
  InternalSigningPrepareInput,
  InternalSigningPrepareResponse,
  InternalSigningCaptureInput,
  InternalSigningCaptureResponse,
  InternalSigningCompleteInput,
  InternalSigningCompleteResponse,
  CreatePresentationRequest,
  CreatePresentationResponse,
  SetPresentationStepRequest,
  PresentationStateResponse,
  OfficerPackageManifestResponse,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

export class SigningApi {
  public constructor(private readonly http: ApiHttpClient) {}

  // ---------------------------------------------------------------------------
  // Prepare / cache
  // ---------------------------------------------------------------------------

  public preparePackage(
    req: PreparePackageRequest,
    opts?: RequestOptions
  ): Promise<PreparePackageResult> {
    return this.http.post<PreparePackageResult>('/signing/prepare-package', { ...opts, body: req });
  }

  public getPrepareStatus(jobId: string, opts?: RequestOptions): Promise<PrepareStatusResult> {
    return this.http.get<PrepareStatusResult>(
      `/signing/prepare-status/${encodeURIComponent(jobId)}`,
      { ...opts }
    );
  }

  public cachePdfs(req: CachePdfsRequest, opts?: RequestOptions): Promise<CachePdfsResult> {
    return this.http.post<CachePdfsResult>('/signing/cache-pdfs', { ...opts, body: req });
  }

  // ---------------------------------------------------------------------------
  // Officer package manifest + flattened document preview (REQ-167 phase 2)
  // ---------------------------------------------------------------------------

  /** Manifest of the cart's BUILT package (all docs incl. view-only).
   *  RequireOfficer-gated server-side; 404 PACKAGE_NOT_READY pre-build. */
  public getOfficerPackageManifest(
    cartId: string,
    opts?: RequestOptions
  ): Promise<OfficerPackageManifestResponse> {
    return this.http.get<OfficerPackageManifestResponse>('/v3/signing/internal/document', {
      ...opts,
      query: { cartId },
    });
  }

  /** One built document's FLATTENED bytes (prior signatures overlaid) for an
   *  officer preview iframe. Server refuses unflattenable PDFs (fail-closed). */
  public getOfficerDocumentBlob(
    cartId: string,
    documentId: string,
    opts?: RequestOptions
  ): Promise<Blob> {
    return this.http.getBlob('/v3/signing/internal/document', {
      ...opts,
      query: { cartId, documentId },
    });
  }

  // ---------------------------------------------------------------------------
  // Sessions / invites
  // ---------------------------------------------------------------------------

  public createSession(
    req: CreateSigningSessionRequest,
    opts?: RequestOptions
  ): Promise<CreateSigningSessionResponse> {
    return this.http.post<CreateSigningSessionResponse>('/signing/create-session', {
      ...opts,
      body: req,
    });
  }

  public start(req: StartSigningRequest, opts?: RequestOptions): Promise<StartSigningResponse> {
    return this.http.post<StartSigningResponse>('/signing/start', { ...opts, body: req });
  }

  public sendInvite(req: SendInviteRequest, opts?: RequestOptions): Promise<InviteResult> {
    return this.http.post<InviteResult>('/signing/send-invite', { ...opts, body: req });
  }

  public getSessionStatus(
    sessionId: string,
    opts?: RequestOptions
  ): Promise<SigningSessionStatusResponse> {
    return this.http.get<SigningSessionStatusResponse>(
      `/signing/session-status/${encodeURIComponent(sessionId)}`,
      { ...opts }
    );
  }

  public downloadSignedPdf(
    documentIdOrGroupId: string,
    opts?: RequestOptions
  ): Promise<DownloadResult> {
    return this.http.get<DownloadResult>(
      `/signing/download/${encodeURIComponent(documentIdOrGroupId)}`,
      { ...opts }
    );
  }

  // ---------------------------------------------------------------------------
  // QR + embedded
  // ---------------------------------------------------------------------------

  public generateSigningLink(
    req: GenerateQrRequest,
    opts?: RequestOptions
  ): Promise<SigningLinkResult> {
    return this.http.post<SigningLinkResult>('/signing/generate-qr', { ...opts, body: req });
  }

  public createEmbeddedInvites(
    req: CreateEmbeddedInvitesRequest,
    opts?: RequestOptions
  ): Promise<EmbeddedInvitesResponse> {
    return this.http.post<EmbeddedInvitesResponse>('/signing/create-embedded-invite', {
      ...opts,
      body: req,
    });
  }

  public regenerateEmbeddedInviteLink(
    req: EmbeddedInviteLinkRequest,
    opts?: RequestOptions
  ): Promise<EmbeddedInviteLinkResponse> {
    return this.http.post<EmbeddedInviteLinkResponse>('/signing/embedded-invite-link', {
      ...opts,
      body: req,
    });
  }

  // ---------------------------------------------------------------------------
  // Wet-sign complete (422 body shape differs from success — surfaces via ApiError)
  // ---------------------------------------------------------------------------

  public completeWetSign(
    req: WetCompleteRequest,
    opts?: RequestOptions
  ): Promise<WetCompleteSuccessResponse> {
    return this.http.post<WetCompleteSuccessResponse>('/signing/wet-complete', {
      ...opts,
      body: req,
    });
  }

  /**
   * Submit a wet-sign attestation — the FSR's affirmation that the customer's
   * wet signature on the scanned page matches their on-file ID, the witnessed
   * date matches the document date, etc. Server stores the attestation row +
   * binds it to the cart/session/signer triple for audit.
   *
   * 422 body uses the same `{success:false, reason, error}` shape as
   * /signing/wet-complete and is surfaced via ApiError.details.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public submitWetSignAttestation(req: unknown, opts?: RequestOptions): Promise<any> {
    return this.http.post('/signing/wet-attestation', { ...opts, body: req });
  }

  // ---------------------------------------------------------------------------
  // Corrections (doc replacement — legacy at /signing/correction)
  // ---------------------------------------------------------------------------

  public requestCorrection(
    req: CorrectionRequestInput,
    opts?: RequestOptions
  ): Promise<CorrectionRequestResult> {
    return this.http.post<CorrectionRequestResult>('/signing/correction', { ...opts, body: req });
  }

  public getCorrectionStatus(
    correctionId: string,
    opts?: RequestOptions
  ): Promise<CorrectionStatusResult> {
    return this.http.get<CorrectionStatusResult>(
      `/signing/correction/${encodeURIComponent(correctionId)}`,
      { ...opts }
    );
  }

  public listCorrectionsForSession(
    sessionId: string,
    opts?: RequestOptions
  ): Promise<CorrectionHistoryResult> {
    return this.http.get<CorrectionHistoryResult>('/signing/correction', {
      ...opts,
      query: { sessionId },
    });
  }

  // ---------------------------------------------------------------------------
  // v3 correct-signer — mid-session signer replacement.
  //
  // Endpoint fix (2026-04-21, Wave-2 audit H-RSC-12): the Function app does NOT
  // host `/v3/signing/correct-signer` (never shipped). The correction flow
  // lives on `SignNowCorrectionFunction.cs` at `/signing/correction`
  // (POST) + `/signing/correction/{id}` (GET). This client method posts the
  // mid-session signer-replacement payload to that real route so callers get
  // 2xx instead of 404. See docs/api/endpoints/signing.md §Correction endpoints.
  // ---------------------------------------------------------------------------

  public correctSigner(
    req: CorrectSignerInput,
    opts?: RequestOptions
  ): Promise<CorrectSignerResult> {
    return this.http.post<CorrectSignerResult>('/signing/correction', { ...opts, body: req });
  }
}

// -----------------------------------------------------------------------------
// Phase 1 — v3 signing namespace (Bill 2026-05-10).
//
// `RetailSigningApi` mounts as `client.retail.signing.*` and groups the two new
// sub-namespaces that the internal click-to-sign + canvas primitives use:
//
//   • `client.retail.signing.esign.*`    — ESIGN consent gate (status / text / record)
//   • `client.retail.signing.internal.*` — Internal capture (prepare / capture)
//
// Endpoint contracts here mirror the sibling-agent specs (fixed 2026-07-01 —
// status/text live under /v3/esign/consent/*, per V3EsignFunction routes;
// the old /v3/esign/status + /v3/esign/text paths 404'd):
//   GET  /v3/esign/consent/status?cartId&signerCifNumber
//   GET  /v3/esign/consent/text
//   POST /v3/esign/consent
//   POST /v3/signing/internal/prepare
//   POST /v3/signing/internal/capture
//   POST /v3/signing/internal/complete
//
// If the sibling backend specs drift, only this file needs to flip — every
// component imports through the wrappers, not raw fetch.
// -----------------------------------------------------------------------------

export class EsignApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /**
   * Fetch the signer's ESIGN consent status. Gate component calls this on
   * mount; passes through immediately when `hasConsent && !requiresFreshConsent`.
   */
  public getStatus(
    req: { cartId: string; signerCifNumber: string },
    opts?: RequestOptions
  ): Promise<EsignConsentStatus> {
    return this.http.get<EsignConsentStatus>('/v3/esign/consent/status', {
      ...opts,
      query: {
        cartId: req.cartId,
        signerCifNumber: req.signerCifNumber,
      },
    });
  }

  /** Fetch the canonical consent body the gate renders. Server pins version. */
  public async getText(opts?: RequestOptions): Promise<EsignConsentTextResponse> {
    // Wire shape is {success, data:{text, sha256, minScrollProgressPct}} —
    // NOT the {version, title, body} this API promises. The mismatch made the
    // gate throw into its GENERIC fallback copy, so the customer never saw
    // the compliance-approved JotForm consent language (Bill 2026-07-03).
    const raw = await this.http.get<{
      success?: boolean;
      data?: { text?: string; sha256?: string; minScrollProgressPct?: number };
    } & Partial<EsignConsentTextResponse>>('/v3/esign/consent/text', { ...opts });
    if (typeof raw?.body === 'string' && raw.body.length > 0) {
      return raw as EsignConsentTextResponse; // future flat shape — pass through
    }
    const text = raw?.data?.text ?? '';
    if (!text) throw new Error('esign consent text unavailable');
    const firstLine = text.split('\n', 1)[0]?.trim() ?? '';
    return {
      version: raw?.data?.sha256 ? `sha256:${raw.data.sha256.slice(0, 12)}` : 'server',
      title: firstLine || 'Consent for Electronic Transactions, Records, and Signatures',
      body: text,
    };
  }

  /** Record the customer's I-agree click + the audit context (UA, viewport, scroll). */
  public recordConsent(
    req: EsignConsentRecordInput,
    opts?: RequestOptions
  ): Promise<EsignConsentRecordResponse> {
    return this.http.post<EsignConsentRecordResponse>('/v3/esign/consent', {
      ...opts,
      body: req,
    });
  }
}

export class InternalSigningApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /**
   * Open the next signing slot for a signer. Returns the per-doc capture
   * mode (Q1 doc-role tiering) so the client renders click/typed/drawn
   * in the right order.
   */
  public async prepare(
    req: InternalSigningPrepareInput,
    opts?: RequestOptions
  ): Promise<InternalSigningPrepareResponse> {
    // 2026-07-02 — the Function wraps SUCCESS payloads as {success, data:{…}}
    // while failures are flat {success:false, error, code}. Normalize to the
    // flat shape components consume (this mismatch made a READY package render
    // as "not ready" in the live walk).
    const raw = await this.http.post<
      InternalSigningPrepareResponse & { data?: Omit<InternalSigningPrepareResponse, 'success'>; code?: string }
    >('/v3/signing/internal/prepare', { ...opts, body: req });
    if (raw && raw.data) return { success: raw.success, ...raw.data };
    if (raw && raw.code && !raw.errorCode) return { ...raw, errorCode: raw.code };
    return raw;
  }

  /**
   * Capture a completed signature for one document. Server writes the
   * SignedDocumentAudit row + stamps the audit page + uploads the signed
   * PDF to ao_Documents.
   */
  public async capture(
    req: InternalSigningCaptureInput,
    opts?: RequestOptions
  ): Promise<InternalSigningCaptureResponse> {
    // Same envelope normalization as prepare() — see note there.
    const raw = await this.http.post<
      InternalSigningCaptureResponse & { data?: Omit<InternalSigningCaptureResponse, 'success'>; code?: string }
    >('/v3/signing/internal/capture', { ...opts, body: req });
    if (raw && raw.data) return { success: raw.success, ...raw.data };
    if (raw && raw.code && !raw.errorCode) return { ...raw, errorCode: raw.code };
    return raw;
  }

  /**
   * Punch-list S3.1 (2026-06-11) — close out an internal SigningSession.
   * Called by the dispatcher once every internal-engine (signer × doc) pair
   * has captured. Server flips SigningSession.Status → 'completed', emits
   * the 'signing-internal-completed' SessionEvent and enqueues the pipeline
   * resume (step index 8). Idempotent — replays return transitioned=false.
   *
   * Backend route: InternalSigningFunction.Complete
   * (POST /v3/signing/internal/complete, Officer+ role gate).
   */
  /**
   * BACKLOG-1761 (Bill 2026-10-15) — save the officer's signing plan (who signs in branch, who
   * signs remotely) on its own route. The cart update route refuses it once the cart is locked
   * for signing. Backend: SigningPlanFunction (POST /v3/signing/plan, Officer+).
   */
  public savePlan(
    req: { cartId: string; signingConfig: unknown },
    opts?: RequestOptions
  ): Promise<{ success: boolean; version?: number; error?: string; errorCode?: string }> {
    return this.http.post('/v3/signing/plan', { ...opts, body: req });
  }

  public complete(
    req: InternalSigningCompleteInput,
    opts?: RequestOptions
  ): Promise<InternalSigningCompleteResponse> {
    return this.http.post<InternalSigningCompleteResponse>('/v3/signing/internal/complete', {
      ...opts,
      body: req,
    });
  }

  /**
   * Regenerate-disclosures (Bill 2026-07-01) — abort the cart's signing
   * session and drop it back to Review so the FSR can edit + re-submit for a
   * FRESH document package. Sessions cancel (audit rows preserved); SignNow
   * invites are voided best-effort; captured signatures are voided in effect.
   *
   * 409 SIGNATURES_ALREADY_CAPTURED (with signedCount) means the caller must
   * confirm with the FSR and re-call with confirmVoidSignatures=true.
   */
  public regenerate(
    cartId: string,
    body: { confirmVoidSignatures?: boolean; reason?: string } = {},
    opts?: RequestOptions
  ): Promise<SigningRegenerateResponse> {
    return this.http.post<SigningRegenerateResponse>(
      `/v3/carts/${encodeURIComponent(cartId)}/signing/regenerate`,
      { ...opts, body },
    );
  }
}

export interface SigningRegenerateResponse {
  success: boolean;
  cartId?: string;
  /** 'active' on success — the cart is editable + re-submittable again. */
  status?: string;
  sessionsCancelled?: number;
  signaturesVoided?: number;
  note?: string;
  error?: string;
  code?: string;
  /** Present on the 409 confirmation handshake. */
  signedCount?: number;
}

/**
 * Cross-device signing PRESENTATION (Bill 2026-06-24) — the FSR control-screen
 * side of the two-screen ASUS signer surface (and Phase-2 remote/RCP). The
 * signer kiosk itself posts the token-only /v3/signing/kiosk/* routes directly
 * (no staff bearer), so those aren't exposed here.
 */
export class SigningPresentationApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /** Mint a presentation + capability token (token returned once — build the kiosk URL with it). */
  public create(
    req: CreatePresentationRequest,
    opts?: RequestOptions
  ): Promise<CreatePresentationResponse> {
    return this.http.post<CreatePresentationResponse>('/v3/signing/present/create', { ...opts, body: req });
  }

  /**
   * Which cart signers each SIGNATURE document in the cart's package is assigned to, by the
   * server's own rule (the one step-push and capture enforce). The FSR panel plans only these
   * pairs. `assignments` is null when there is no package to judge. pcb-retail-api 3.23.13+.
   */
  public assignments(
    req: { cartId: string; signers: ReadonlyArray<{ signerIndex: number; cifNumber: string | null }> },
    opts?: RequestOptions
  ): Promise<{ success: boolean; assignments?: Readonly<Record<string, ReadonlyArray<number>>> | null; error?: string }> {
    return this.http.post<{ success: boolean; assignments?: Readonly<Record<string, ReadonlyArray<number>>> | null; error?: string }>(
      '/v3/signing/present/assignments', { ...opts, body: req });
  }

  /** Push the current step (signer × document × mode) for the kiosk to render. */
  public setStep(
    presentationId: string,
    req: SetPresentationStepRequest,
    opts?: RequestOptions
  ): Promise<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `/v3/signing/present/${encodeURIComponent(presentationId)}/step`,
      { ...opts, body: req }
    );
  }

  /** Control-screen poll — current status + step + last capture. */
  public get(
    presentationId: string,
    opts?: RequestOptions
  ): Promise<PresentationStateResponse> {
    return this.http.get<PresentationStateResponse>(
      `/v3/signing/present/${encodeURIComponent(presentationId)}`,
      { ...opts }
    );
  }

  /** Close the presentation. */
  public cancel(
    presentationId: string,
    opts?: RequestOptions
  ): Promise<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `/v3/signing/present/${encodeURIComponent(presentationId)}/cancel`,
      { ...opts }
    );
  }

  /**
   * Reconnect a disconnected signer (BACKLOG-0875) — mint a FRESH capability
   * token for the SAME presentation (same cart/session/current step). The old
   * link stops working; the returned token/link/pairCode reconnect the kiosk.
   */
  public reissue(
    presentationId: string,
    opts?: RequestOptions
  ): Promise<ReissuePresentationResponse> {
    return this.http.post<ReissuePresentationResponse>(
      `/v3/signing/present/${encodeURIComponent(presentationId)}/reissue`,
      { ...opts }
    );
  }
}

export interface ReissuePresentationResponse {
  success: boolean;
  presentationId?: string;
  token?: string;
  pairCode?: string;
  link?: string;
  /** Fresh access code — non-null only for a remote link. */
  accessCode?: string | null;
  expiresAt?: string;
  status?: string;
  error?: string;
  errorCode?: string;
}

/** Remote signing invite (BACKLOG-0832) — one emailed secure link + OTP. */
export interface RemoteInviteRequest {
  cartId: string;
  signingSessionId?: string;
  signerIndex: number;
  signerName: string;
  signerCif?: string;
  email: string;
  ttlHours?: number;
}
export interface RemoteInviteResponse {
  success: boolean;
  presentationId?: string;
  link?: string;
  /** One-time access code — returned to the OFFICER to relay out-of-band. */
  accessCode?: string;
  expiresAt?: string;
  emailSent?: boolean;
  emailError?: string | null;
  error?: string;
  errorCode?: string;
}

/**
 * Remote-signing initiate (BACKLOG-0832, Bill 2026-07-13 Item 23). Mints an
 * emailed secure link + a one-time access code the FSR relays to the signer
 * out-of-band. Backed by POST /v3/signing/remote/invite.
 */
export class RemoteSigningApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public invite(req: RemoteInviteRequest, opts?: RequestOptions): Promise<RemoteInviteResponse> {
    return this.http.post<RemoteInviteResponse>('/v3/signing/remote/invite', { ...opts, body: req });
  }

  /**
   * Retire the signer's link and e-mail a fresh one (2026-10-06) — optionally to a corrected address,
   * optionally worded as a reminder. The response carries the new presentationId, link and send status.
   */
  public reissue(
    presentationId: string,
    body: { email?: string; reminder?: boolean },
    opts?: RequestOptions,
  ): Promise<RemoteInviteResponse & { supersededPresentationId?: string; email?: string }> {
    return this.http.post<RemoteInviteResponse & { supersededPresentationId?: string; email?: string }>(
      `/v3/signing/remote/invites/${encodeURIComponent(presentationId)}/resend`, { ...opts, body });
  }

  /** The cart's open remote invites (no links — only their hashes are stored). For managing invites
   *  after the cart session is closed (2026-10-06). */
  public listOpen(cartId: string, opts?: RequestOptions): Promise<{
    success: boolean;
    invites?: Array<{ presentationId: string; signerIndex: number | null; name: string | null; email: string | null;
      status: string; inviteSentAt: string | null; expiresAt: string; emailVerifiedAt: string | null;
      /** BACKLOG-1760: where the signer is (sent → checking-email → signing → finished). */
      stage?: 'sent' | 'checking-email' | 'signing' | 'finished';
      signedCount?: number;
      /** Documents this signer must sign, once they have opened the link (null before). */
      toSignCount?: number | null;
      lastActivityAt?: string }>;
    error?: string;
  }> {
    return this.http.get(`/v3/signing/remote/invites?cartId=${encodeURIComponent(cartId)}`, { ...opts });
  }

  /** Reminder to the remote signer — since 2026-10-06 it carries a FRESH link (the old one stops working). */
  public nudge(presentationId: string, opts?: RequestOptions): Promise<RemoteInviteActionResponse> {
    return this.http.post<RemoteInviteActionResponse>(
      `/v3/signing/remote/invites/${encodeURIComponent(presentationId)}/nudge`, { ...opts, body: {} });
  }

  /** BACKLOG-1673 — retire the old link and mint a NEW link + access code (returned to the officer). */
  public resend(presentationId: string, opts?: RequestOptions): Promise<RemoteInviteResponse & { supersededPresentationId?: string }> {
    return this.http.post<RemoteInviteResponse & { supersededPresentationId?: string }>(
      `/v3/signing/remote/invites/${encodeURIComponent(presentationId)}/resend`, { ...opts, body: {} });
  }

  /** BACKLOG-1673 — cancel the remote request; the officer's task closes, the document stays unsigned. */
  public cancel(presentationId: string, reason?: string, opts?: RequestOptions): Promise<RemoteInviteActionResponse> {
    return this.http.post<RemoteInviteActionResponse>(
      `/v3/signing/remote/invites/${encodeURIComponent(presentationId)}/cancel`, { ...opts, body: { reason } });
  }
}

export interface RemoteInviteActionResponse {
  success: boolean;
  presentationId?: string;
  status?: string;
  emailSent?: boolean;
  emailError?: string | null;
  error?: string;
  errorCode?: string;
}

/**
 * Root `client.retail.signing.*` namespace. Composes the esign + internal +
 * presentation + remote sub-namespaces so the consent gate + capture
 * primitives + cross-device surface + remote-invite share one root.
 */
export class RetailSigningApi {
  public readonly esign: EsignApi;
  public readonly internal: InternalSigningApi;
  public readonly presentation: SigningPresentationApi;
  public readonly remote: RemoteSigningApi;

  public constructor(http: ApiHttpClient) {
    this.esign = new EsignApi(http);
    this.internal = new InternalSigningApi(http);
    this.presentation = new SigningPresentationApi(http);
    this.remote = new RemoteSigningApi(http);
  }
}
